import { CheckIcon, HelpCircleIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { Combobox } from '@/components/combobox/combobox'
import { DatePicker } from '@/components/date-picker/date-picker'
import { Form, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'

import { cn } from '@/lib/utils'
import { getLocalDate, getUtcDateString } from '@/lib/utils/datetime'

import { ExistingApplicantNameInput } from './existing-applicant-name-input/existing-applicant-name-input'
import { ExtractedInfoGuideDialog } from './extracted-info-guide-dialog/extracted-info-guide-dialog'
import { FormLabelWithNewBadge } from './form-label-with-new-badge/form-label-with-new-badge'
import { useCurrentDocumentForms } from './use-current-document-forms'

export const CurrentDocumentForms = () => {
  const {
    documentOCRResultForm,
    applicantCandidateList,
    serviceCenterList,
    caseCategoryList,
    hasMatchingApplicant,
    isReviewed,
    isDisabledReloadButton,
    isDisabledCompleteReviewButton,
    isOpenExtractedInfoGuideDialog,
    isReloadingCaseData,
    isUpdatingOCRReviewStatus,
    openExtractInfoGuideDialog,
    handleChangeExtractedInfoGuideDialogOpen,
    handleChangeCaseNumber,
    handleChangeNewApplicantName,
    handleChangeExistingOcrScannedApplicantName,
    handleChangeExistingMatchingApplicantId,
    handleChangeApplicationFiledDate,
    handleChangeCaseType,
    handleChangeServiceCenter,
    handleChangeNationality,
    handleSelectPriorityDate,
    handleSelectValidFrom,
    handleSelectValidTo,
    handleSelectRfeDueDate,
    handleSelectInterviewDate,
    handleSelectBiometricDate,
    handleChangeApplicantEmail,
    handleChangeApplicantPhone,
    handleChangeCaseTags,
    handleChangeCaseCategory,
    handleClickReloadButton,
    handleClickCompleteReviewButton,
  } = useCurrentDocumentForms()

  return (
    <>
      <div className={cn('flex gap-x-5', 'h-full')}>
        <div className={cn('w-[350px]')}>
          <div className={cn('px-4 py-5', 'rounded-[10px]', 'bg-white')}>
            <h3 className={cn('flex justify-between')}>
              <p className={cn('text-sm font-semibold text-black')}>*Info Extracted</p>
              <button type="button" onClick={openExtractInfoGuideDialog}>
                <HelpCircleIcon size={16} className={cn('text-black')} />
              </button>
            </h3>
            <p className={cn('text-xs font-normal text-slate-500', 'mt-1')}>
              Key details from the document — please review.
            </p>

            <Card className={cn('mt-2.5')}>
              <CardContent className={cn('px-4 py-[14px]')}>
                <Form {...documentOCRResultForm}>
                  <FormField
                    control={documentOCRResultForm.control}
                    name="caseNumber"
                    rules={{
                      validate: {
                        required: (v) => (v.value && v.value?.length > 0) || 'Case Number is required',
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabelWithNewBadge isNew={field?.value?.isNew}>Case Number*</FormLabelWithNewBadge>
                        <div className={cn('flex gap-x-1')}>
                          <Input
                            rootClassName={cn('flex-1')}
                            {...field}
                            disabled={isReviewed}
                            value={field?.value.value ?? ''}
                            errorMessage={fieldState.error?.message}
                            onChange={(e) => {
                              const newValue = e.target.value.replace(/\s/g, '').toUpperCase()
                              field.onChange({
                                ...field.value,
                                value: newValue,
                              })
                              handleChangeCaseNumber(newValue)
                            }}
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={isDisabledReloadButton}
                            loading={isReloadingCaseData}
                            className={cn('text-blue-600')}
                            onClick={handleClickReloadButton}
                          >
                            Reload
                          </Button>
                        </div>
                      </FormItem>
                    )}
                  />
                </Form>
              </CardContent>
            </Card>

            <Card className={cn('mt-2')}>
              <CardContent className={cn('px-4 py-[14px]')}>
                <Form {...documentOCRResultForm}>
                  <div className={cn('flex flex-col gap-y-2.5')}>
                    <FormField
                      control={documentOCRResultForm.control}
                      name="applicantNameCandidate"
                      render={({ field }) =>
                        !hasMatchingApplicant ? (
                          <FormItem>
                            <FormLabelWithNewBadge isNew>Applicant Name</FormLabelWithNewBadge>
                            <Input
                              {...field}
                              disabled={isReviewed}
                              value={field?.value.ocrScannedApplicantNameWhenNew ?? ''}
                              onChange={(e) => {
                                field.onChange({
                                  ...field.value,
                                  ocrScannedApplicantNameWhenNew: e.target.value,
                                })
                                handleChangeNewApplicantName(e.target.value)
                              }}
                            />
                          </FormItem>
                        ) : (
                          <FormItem>
                            <ExistingApplicantNameInput
                              {...field}
                              disabled={isReviewed}
                              applicantCandidateList={applicantCandidateList}
                              ocrScannedValue={field?.value.ocrScannedApplicantNameWhenExisting ?? ''}
                              matchingValue={field?.value.matchingApplicantId ?? ''}
                              onChangeScannedValue={(value) => {
                                field.onChange({
                                  ...field.value,
                                  ocrScannedApplicantNameWhenExisting: value,
                                })
                                handleChangeExistingOcrScannedApplicantName(value)
                              }}
                              onChangeMatchingValue={(value) => {
                                field.onChange({
                                  ...field.value,
                                  matchingApplicantId: value,
                                })
                                handleChangeExistingMatchingApplicantId(value)
                              }}
                            />
                          </FormItem>
                        )
                      }
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="applicationFiledDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Date Filed</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value?.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleChangeApplicationFiledDate(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="caseType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Case Type</FormLabelWithNewBadge>
                          <Input
                            {...field}
                            disabled={isReviewed}
                            value={field?.value.value ?? ''}
                            onChange={(e) => {
                              field.onChange({
                                ...field.value,
                                value: e.target.value,
                              })
                              handleChangeCaseType(e.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="serviceCenter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Service Center</FormLabelWithNewBadge>
                          <Combobox
                            {...field}
                            disabled={isReviewed}
                            placeholder="Select"
                            items={
                              serviceCenterList?.map(({ code, description }) => ({
                                label: description,
                                value: code,
                              })) ?? []
                            }
                            value={field?.value.value ?? ''}
                            onValueChange={(value) => {
                              field.onChange({
                                ...field.value,
                                value,
                              })
                              handleChangeServiceCenter(value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="nationality"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Nationality</FormLabelWithNewBadge>
                          <Input
                            {...field}
                            disabled={isReviewed}
                            value={field?.value.text ?? ''}
                            onChange={(e) => {
                              field.onChange({
                                ...field.value,
                                text: e.target.value,
                              })
                              handleChangeNationality(e.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="priorityDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Priority Date</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectPriorityDate(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="validFrom"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Valid From</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectValidFrom(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="validTo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Valid To</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectValidTo(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="rfeDueDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>
                            RFE Response Due Date
                          </FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectRfeDueDate(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="interviewDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value.isNew}>Interview Date</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectInterviewDate(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="biometricDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabelWithNewBadge isNew={field?.value?.isNew}>Biometric Date</FormLabelWithNewBadge>
                          <DatePicker
                            {...field}
                            disabled={isReviewed}
                            placeholder="MM/DD/YYYY"
                            formatString="MM/DD/YYYY"
                            value={field?.value.value ? getLocalDate(field.value.value) : undefined}
                            onSelect={(date) => {
                              const dateString = date ? getUtcDateString(date, 'YYYY-MM-DD') : undefined
                              field.onChange({
                                ...field.value,
                                value: dateString,
                              })
                              handleSelectBiometricDate(dateString)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className={cn('w-[350px]')}>
          <div className={cn('flex-1', 'px-4 py-5', 'rounded-[10px]', 'bg-white')}>
            <h3 className={cn('text-sm font-semibold text-black')}>Additional Information</h3>
            <p className={cn('text-xs font-normal text-slate-500', 'mt-1')}>
              Please review the additional information.
            </p>

            <Card className={cn('mt-1.5')}>
              <CardContent className={cn('px-4 py-[14px]')}>
                <Form {...documentOCRResultForm}>
                  <div className={cn('flex flex-col gap-y-3')}>
                    <FormField
                      control={documentOCRResultForm.control}
                      name="caseAdditionalData.email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn('text-xs text-slate-700')}>Email Address</FormLabel>
                          <Input
                            {...field}
                            disabled={isReviewed}
                            placeholder="abc@lawfully.com"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              handleChangeApplicantEmail(e.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="caseAdditionalData.phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn('text-xs text-slate-700')}>Phone Number</FormLabel>
                          <Input
                            {...field}
                            disabled={isReviewed}
                            placeholder="123 456 789"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              handleChangeApplicantPhone(e.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="caseAdditionalData.caseTags"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn('text-xs text-slate-700')}>Case Tags</FormLabel>
                          <Input
                            {...field}
                            disabled={isReviewed}
                            placeholder="e.g. Workers, Refugees"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              handleChangeCaseTags(e.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentOCRResultForm.control}
                      name="caseAdditionalData.caseCategory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={cn('text-xs text-slate-700')}>Form Category</FormLabel>
                          <Combobox
                            {...field}
                            disabled={isReviewed}
                            items={
                              caseCategoryList?.map(({ alias, code, description }) => ({
                                label: `${alias ? `${alias} - ` : ''}${description}`,
                                value: code,
                              })) ?? []
                            }
                            placeholder="Category"
                            emptyPlaceholder={
                              <div className={cn('px-2.5')}>
                                <p>No items.</p>
                                <p className={cn('text-xs text-slate-400', 'my-2')}>OR</p>
                                <p>Please check if case type is set properly.</p>
                              </div>
                            }
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value)
                              handleChangeCaseCategory(value)
                            }}
                          />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </CardContent>
            </Card>
          </div>

          <Button
            variant={isReviewed ? 'outline' : 'default'}
            className={cn('w-full', 'text-white', 'mt-4', {
              'border-blue-600 bg-blue-100 text-blue-600 hover:bg-blue-50 hover:text-blue-600': isReviewed,
            })}
            loading={isUpdatingOCRReviewStatus}
            disabled={isDisabledCompleteReviewButton}
            onClick={handleClickCompleteReviewButton}
          >
            {isReviewed && <CheckIcon size={16} />}
            <span className={cn('ml-2')}>{`${isReviewed ? 'Review Completed' : 'Complete Review'}`}</span>
          </Button>
        </div>
      </div>

      <ExtractedInfoGuideDialog
        open={isOpenExtractedInfoGuideDialog}
        onChangeOpen={handleChangeExtractedInfoGuideDialogOpen}
      />
    </>
  )
}
